import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../../api/leaveAPI';
import Spinner from '../../spinner';
import { toast } from 'react-toastify';
import './leaveDetailsModal.css';

export default function LeaveDetailsModal({
    isOpen,
    onClose,
    leaveData,
    showBalanceInfo = true
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [balanceInfo, setBalanceInfo] = useState({
        allocatedLeaves: 0,
        takenLeaves: 0,
        pendingLeaves: 0,
        partiallyApprovedLeaves: 0,
        balanceLeaves: 0
    });

    const [expandedSections, setExpandedSections] = useState({
        basic: true,
        balance: false,
        approval: false,
        timeline: false
    });

    useEffect(() => {
        if (isOpen && leaveData && showBalanceInfo) {
            loadBalanceInfo();
        }
    }, [isOpen, leaveData]);

    const loadBalanceInfo = async () => {
        if (!leaveData.employeeCode || !leaveData.leaveTypeCode) return;

        setIsLoading(true);
        try {
            const balanceData = await leaveAPI.getBalanceLeaveByLeaveType(
                leaveData.employeeCode,
                leaveData.leaveTypeCode,
                leaveData.pCode,
                leaveData.startDate
            );

            setBalanceInfo({
                allocatedLeaves: balanceData.allocatedLeaves,
                takenLeaves: balanceData.takenLeaves,
                pendingLeaves: balanceData.pendingLeaves,
                partiallyApprovedLeaves: balanceData.partiallyApprovedLeaves,
                balanceLeaves: balanceData.allocatedLeaves - (balanceData.takenLeaves + balanceData.pendingLeaves)
            });
        } catch (error) {
            toast.error('Failed to load balance information');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateLeaveDays = () => {
        if (!leaveData.startDate || !leaveData.endDate) return 0;
        const start = new Date(leaveData.startDate);
        const end = new Date(leaveData.endDate);
        return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'Approved': 'success',
            'Pending': 'warning',
            'Rejected': 'danger'
        };
        return statusMap[status] || 'secondary';
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show d-block leave-details-modal" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header bg-light border-bottom">
                        <div className="d-flex align-items-center">
                            <i className="fas fa-user-clock me-2 text-primary"></i>
                            <div>
                                <h5 className="modal-title">
                                    Leave Details - {leaveData?.employeeName ? `${leaveData.employeeName} (${leaveData.employeeCode}) ` : 'N/A'}
                                </h5>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={isLoading}
                        ></button>
                    </div>

                    <div className="modal-body">
                        {isLoading && (
                            <div className="text-center py-3">
                                <Spinner />
                                <p className="mt-2">Loading leave details...</p>
                            </div>
                        )}

                        <div className={`leave-details-content ${isLoading ? 'd-none' : ''}`}>
                            {/* Basic Information Section */}
                            <div className="card mb-3">
                                <div
                                    className="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                    onClick={() => toggleSection('basic')}
                                >
                                    <h6 className="mb-0">
                                        <i className="fas fa-user me-2"></i>
                                        Basic Information
                                    </h6>
                                    <i className={`fas fa-chevron-${expandedSections.basic ? 'up' : 'down'}`}></i>
                                </div>

                                {expandedSections.basic && (
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="info-item">
                                                    <strong>Employee:</strong>
                                                    <span className="ms-2">
                                                        ({leaveData?.employeeCode}) {leaveData?.employeeName}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <strong>Leave Type:</strong>
                                                    <span className="ms-2">
                                                        {leaveData?.leaveTypeName}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <strong>Leave Period:</strong>
                                                    <span className="ms-2">
                                                        {formatDate(leaveData?.startDate)} - {formatDate(leaveData?.endDate)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="info-item">
                                                    <strong>Duration:</strong>
                                                    <span className="ms-2 badge bg-info">
                                                        {calculateLeaveDays()} day(s)
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <strong>Status:</strong>
                                                    <span className={`ms-2 badge bg-${getStatusBadge(leaveData?.transType)}`}>
                                                        {leaveData?.transType || 'Pending'}
                                                    </span>
                                                </div>
                                                <div className="info-item">
                                                    <strong>Leave Type:</strong>
                                                    <span className="ms-2">
                                                        {leaveData?.isShortLeave ? '⏱️ Short Leave' :
                                                            leaveData?.isHalfDay ? '🕐 Half Day' : '📅 Full Day'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {leaveData?.reason && (
                                            <div className="mt-3">
                                                <strong>Reason:</strong>
                                                <div className="reason-box p-2 mt-1 bg-light rounded">
                                                    {leaveData.reason}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Leave Balance Section */}
                            {showBalanceInfo && (
                                <div className="card mb-3">
                                    <div
                                        className="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                        onClick={() => toggleSection('balance')}
                                    >
                                        <h6 className="mb-0">
                                            <i className="fas fa-chart-pie me-2"></i>
                                            Leave Balance Information
                                        </h6>
                                        <i className={`fas fa-chevron-${expandedSections.balance ? 'up' : 'down'}`}></i>
                                    </div>

                                    {expandedSections.balance && (
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <div className="balance-card text-center p-3 bg-primary text-white rounded">
                                                        <div className="balance-number">{balanceInfo.allocatedLeaves}</div>
                                                        <div className="balance-label">Allocated</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <div className="balance-card text-center p-3 bg-success text-white rounded">
                                                        <div className="balance-number">{balanceInfo.takenLeaves}</div>
                                                        <div className="balance-label">Taken</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <div className="balance-card text-center p-3 bg-warning text-white rounded">
                                                        <div className="balance-number">{balanceInfo.pendingLeaves}</div>
                                                        <div className="balance-label">Pending</div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 mb-3">
                                                    <div className="balance-card text-center p-3 bg-info text-white rounded">
                                                        <div className="balance-number">{balanceInfo.balanceLeaves}</div>
                                                        <div className="balance-label">Remaining</div>
                                                    </div>
                                                </div>
                                            </div>                                            
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Approval Information Section */}
                            {leaveData?.reason && (
                                <div className="card mb-3">
                                    <div
                                        className="card-header bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                        onClick={() => toggleSection('approval')}
                                    >
                                        <h6 className="mb-0">
                                            <i className="fas fa-clipboard-check me-2"></i>
                                            Approval Information
                                        </h6>
                                        <i className={`fas fa-chevron-${expandedSections.approval ? 'up' : 'down'}`}></i>
                                    </div>

                                    {expandedSections.approval && (
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="info-item">
                                                        <strong>Leave Reason:</strong>
                                                        <div className="reason-box p-2 mt-1 bg-light rounded">
                                                            {leaveData.employee_Reason || leaveData.reason}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    {leaveData.reason && leaveData.reason !== leaveData.employee_Reason && (
                                                        <div className="info-item">
                                                            <strong>Approver Comments:</strong>
                                                            <div className="reason-box p-2 mt-1 bg-warning bg-opacity-10 rounded">
                                                                {leaveData.approverComments || 'No comments provided'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}                            
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >                            
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
